import { inject, Injectable } from '@angular/core';
import { Observable, timeout } from 'rxjs';

import { PAYMENT_PROVIDER } from '../payments/payment-provider.token';
import { PAYMENT_COMPLETE_TIMEOUT_MS, PAYMENT_TIMEOUT_MS } from '../payments/payment.config';
import {
  CheckoutIntentResult,
  CheckoutReserveResult,
  CheckoutSessionState,
  PaymentIntentResult,
  PaymentResult,
  PrepurchaseIntentResult,
  PrepurchaseSessionPaymentState,
} from '../payments/payment-provider.interface';
import { PreorderRequest } from '../models/cart.model';
import { TokenService } from './token.service';

/**
 * Fachada de pago. Encapsula la pasarela activa (resuelta por DI vía
 * `PAYMENT_PROVIDER`) y expone `providerId` para que el checkout decida la
 * navegación (D2): solo enruta al paso de pago con tarjeta cuando la pasarela
 * real está activa (`providerId === 'onvo'`).
 *
 * Spec: docs/specs/checkout-payment-step-plan.md §3.4.
 */
@Injectable({ providedIn: 'root' })
export class PaymentService {
  private readonly provider = inject(PAYMENT_PROVIDER);
  private readonly tokenService = inject(TokenService);

  /** Identifica la pasarela activa: `'stub'` (P3) o `'onvo'` (P5). */
  readonly providerId = this.provider.id;

  createIntent(preorderToken: string): Observable<PaymentIntentResult> {
    return this.provider.createIntent(preorderToken).pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  confirm(
    preorderToken: string,
    intentId: string,
    paymentMethodId: string,
  ): Observable<PaymentResult> {
    return this.provider
      .confirm(preorderToken, intentId, paymentMethodId)
      .pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  getStatus(preorderToken: string, intentId: string): Observable<PaymentResult> {
    return this.provider.getStatus(preorderToken, intentId).pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  /** Flujo DIFERIDO: crea el intent de tarjeta desde el carrito (sin crear preorder ni tocar stock). */
  createCheckoutIntent(
    cartId: number,
    payload: PreorderRequest,
  ): Observable<CheckoutIntentResult> {
    return this.provider
      .createCheckoutIntent(cartId, this.withCartToken(payload))
      .pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  /** PUERTA de reserva (checkout manual): abre el HOLD de stock antes del modal de pago. */
  reserveCheckout(
    cartId: number,
    payload: PreorderRequest,
  ): Observable<CheckoutReserveResult> {
    return this.provider
      .reserveCheckout(cartId, this.withCartToken(payload))
      .pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  /** Bootstrap de la pantalla de pago (refresh/enlace directo): estado vivo de la sesión. */
  getCheckoutSession(sessionToken: string): Observable<CheckoutSessionState> {
    return this.provider.getCheckoutSession(sessionToken).pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  /** Tras el pago: verifica con la pasarela y materializa el preorder. Devuelve su token. */
  completeCheckout(sessionToken: string): Observable<{ token: string }> {
    return this.provider.completeCheckout(sessionToken).pipe(timeout(PAYMENT_COMPLETE_TIMEOUT_MS));
  }

  /** Precompra con tarjeta (Bloque 3): crea el intent del 30% sobre la reserva flotante. */
  createPrepurchaseIntent(sessionToken: string): Observable<PrepurchaseIntentResult> {
    return this.provider.createPrepurchaseIntent(sessionToken).pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  /** Bootstrap de la página de pago de precompra (refresh/enlace directo): estado vivo de la reserva. */
  getPrepurchaseSession(sessionToken: string): Observable<PrepurchaseSessionPaymentState> {
    return this.provider.getPrepurchaseSession(sessionToken).pipe(timeout(PAYMENT_TIMEOUT_MS));
  }

  /** Tras el pago: verifica con la pasarela y crea la Prepurchase. Devuelve su id. */
  completePrepurchase(sessionToken: string): Observable<{ prepurchaseId: number }> {
    return this.provider.completePrepurchase(sessionToken).pipe(timeout(PAYMENT_COMPLETE_TIMEOUT_MS));
  }

  /**
   * La API exige el token del carrito en el body de `checkout/{cart}/reserve` y `payment-intent`
   * (nunca en query). El checkout no lo conoce: se añade aquí. Sin token, el payload sale intacto.
   */
  private withCartToken(payload: PreorderRequest): PreorderRequest {
    const token = this.tokenService.getCartToken();
    return token ? { ...payload, token } : payload;
  }
}
